import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Map from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { config } from "../../../config";
import Button from "@mui/material/Button";

const BaseMap = forwardRef(function BaseMap(
    {
        center,
        zoom = 14,
        interactiveLayerIds = [],
        onClick,
        onMouseMove: onMouseMoveProp,
        onMouseLeave: onMouseLeaveProp,
        style,
        children,
    },
    ref
) {
    const mapRef = useRef();
    const [satelliteView, setSatelliteView] = useState(true);
    const [viewState, setViewState] = useState({
        longitude: center.longitude,
        latitude: center.latitude,
        zoom,
    });
    const [hoverInfo, setHoverInfo] = useState(null);

    useImperativeHandle(ref, () => ({
        flyTo: ({ latitude, longitude, zoom }) => {
            setViewState((prev) => ({ ...prev, latitude, longitude, zoom: zoom ?? prev.zoom }));
        },
        fitBounds: (bounds, options) => {
            const map = mapRef.current?.getMap();
            if (!map) return;
            const camera = map.cameraForBounds(bounds, options);
            if (!camera) return;
            setViewState((prev) => ({
                ...prev,
                longitude: camera.center.lng,
                latitude: camera.center.lat,
                zoom: camera.zoom,
            }));
        },
        getMap: () => mapRef.current?.getMap(),
    }));

    const onHover = useCallback(
        (event) => {
            const {
                features,
                point: { x, y },
            } = event;
            const hoveredFeature = features && features[0];
            setHoverInfo(
                (_) =>
                    hoveredFeature &&
                    hoveredFeature.properties.description && {
                        x,
                        y,
                        description: hoveredFeature.properties.description,
                    }
            );
            onMouseMoveProp?.(event);
        },
        [onMouseMoveProp]
    );

    useEffect(() => {
        if (!mapRef.current) {
            setViewState({
                longitude: center.longitude,
                latitude: center.latitude,
                zoom,
            });
            return;
        }

        const map = mapRef.current.getMap();

        map.boxZoom.disable();
        map.dragRotate.disable();
        map.dragPan.disable();
        map.keyboard.disable();
        map.doubleClickZoom.disable();
        map.scrollZoom.disable();
        map.touchZoomRotate.disable();

        const handleMoveEnd = () => {
            map.boxZoom.enable();
            map.dragRotate.enable();
            map.dragPan.enable();
            map.keyboard.enable();
            map.doubleClickZoom.enable();
            map.scrollZoom.enable();
            map.touchZoomRotate.enable();
        };

        map.once("moveend", handleMoveEnd);
        map.flyTo({ center: [center.longitude, center.latitude], zoom, duration: 1500 });

        return () => {
            map.off("moveend", handleMoveEnd);
        };
    }, [JSON.stringify(center), zoom]);

    return (
        <div style={{ position: "relative", ...style }}>
            <Map
                {...viewState}
                ref={mapRef}
                onMove={(evt) => setViewState(evt.viewState)}
                onZoom={(evt) => setViewState(evt.viewState)}
                mapLib={import("mapbox-gl")}
                mapboxAccessToken={config.mapBoxAccessToken}
                mapStyle={satelliteView ? "mapbox://styles/mapbox/satellite-v9" : "mapbox://styles/mapbox/streets-v9"}
                style={{ width: "100%", height: "100%" }}
                interactiveLayerIds={interactiveLayerIds}
                onMouseMove={onHover}
                onMouseLeave={onMouseLeaveProp}
                onClick={onClick}
            >
                {children}
                {hoverInfo && hoverInfo.description && (
                    <div
                        style={{
                            fontSize: "13px",
                            position: "absolute",
                            zIndex: 1,
                            pointerEvents: "none",
                            left: hoverInfo.x + 12,
                            top: hoverInfo.y + 12,
                            backgroundColor: "rgba(0, 0, 0, 0.75)",
                            color: "#ffffff",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            whiteSpace: "pre",
                        }}
                    >
                        {hoverInfo.description}
                    </div>
                )}
            </Map>
            <div style={{ position: "absolute", top: 10, left: 10, zIndex: 4 }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSatelliteView((v) => !v)}
                    style={{ backgroundColor: "white" }}
                >
                    {satelliteView ? "Street View" : "Satellite View"}
                </Button>
            </div>
        </div>
    );
});

export default BaseMap;
